import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAppContext } from '@/hooks/routing'
import { useUserPreferences } from '@/contexts'
import { useSidebarStore } from '@/stores/sidebarStore'
import {
  locationMenu,
  locationBottomItems,
  networkMenu,
  networkBottomItems,
  networkHeroItem,
  resolveNavPath,
  type NavItem,
  type SidebarMenuSection,
} from '@/config/navigation'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { getLocationColor } from '@/lib/locationColors'
import { cn } from '@/lib/utils'
import { useLocationAccess } from '@/hooks/useLocationAccess'
import { normalizeSlug } from '@/lib/slugs'

/** Brand-blue accent shared by the hero pills (Ramp View + Event Planner) and the active menu state. */
const HERO_ACCENT = '#1b449d'

interface SidebarMenuProps {
  collapsed: boolean
}

export function SidebarMenu({ collapsed }: SidebarMenuProps) {
  const location = useLocation()
  const { scope, locationSlug } = useAppContext()
  const { hideComingSoon } = useUserPreferences()
  const {   openSection } = useSidebarStore()
  const { accessibleLocations } = useLocationAccess()

  const isNetwork = scope === 'org'
  const menu = isNetwork ? networkMenu : locationMenu
  const bottomItems = isNetwork ? networkBottomItems : locationBottomItems
  // Location mode shows Ramp View / Event Planner in the QUICK ACCESS section
  // (regular items); only network/org mode keeps a hero pill (Dashboard).
  const heroItem = isNetwork ? networkHeroItem : null

  // Determine accent color
  const currentLoc = accessibleLocations.find(
    (l) => normalizeSlug(l.location_code) === locationSlug
  )
  const accentColor = currentLoc
    ? getLocationColor(currentLoc.location_code)
    : '#1b449d'

  // Get resolved path for a nav item
  const getItemPath = (item: NavItem): string => {
    const basePath = resolveNavPath(item.pathTemplate, locationSlug)
    return item.query ? `${basePath}?${item.query}` : basePath
  }

  // Check if a nav item is active
  const isItemActive = (item: NavItem): boolean => {
    const itemPath = getItemPath(item).split('?')[0]
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/')
  }

  // Auto-expand section containing active item
  useEffect(() => {
    for (const section of menu) {
      if (section.items.some(isItemActive)) {
        openSection(section.id)
      }
    }
  }, [location.pathname, scope, locationSlug])

  // Filter items based on hideComingSoon preference
  const filterItems = (items: NavItem[]): NavItem[] => {
    if (!hideComingSoon) return items
    return items.filter((item) => item.implemented)
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto px-2 pb-2">
      {/* Hero item (network/org mode — Dashboard) */}
      {heroItem && (
        <HeroMenuItem
          item={heroItem}
          active={isItemActive(heroItem)}
          accentColor={HERO_ACCENT}
          collapsed={collapsed}
          href={getItemPath(heroItem)}
        />
      )}

      {/* Menu sections */}
      {menu.map((section) => {
        const items = filterItems(section.items)
        if (items.length === 0) return null

        return (
          <MenuSection
            key={section.id}
            section={section}
            items={items}
            collapsed={collapsed}
            accentColor={accentColor}
            isActive={isItemActive}
            getItemPath={getItemPath}
          />
        )
      })}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom items */}
      {filterItems(bottomItems).length > 0 && (
        <>
          <div className="mx-2.5 my-1.5 h-px bg-border" />
          {filterItems(bottomItems).map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              active={isItemActive(item)}
              collapsed={collapsed}
              href={getItemPath(item)}
            />
          ))}
        </>
      )}
    </div>
  )
}

function HeroMenuItem({
  item,
  active,
  accentColor,
  collapsed,
  href,
}: {
  item: NavItem
  active: boolean
  accentColor: string
  collapsed: boolean
  href: string
}) {
  const Icon = item.icon

  const link = (
    <Link
      to={href}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg border-l-[3px] px-2.5 py-2 mb-1 transition-all no-underline',
        active ? 'border-l-[var(--hero-accent)]' : 'border-l-transparent hover:bg-sidebar-accent/50',
        collapsed && 'justify-center px-0'
      )}
      style={{
        ['--hero-accent' as string]: accentColor,
        background: active ? `${accentColor}15` : undefined,
      }}
    >
      <Icon
        className={cn('h-[15px] w-[15px] shrink-0', !active && 'text-foreground')}
        style={active ? { color: accentColor } : undefined}
        strokeWidth={2}
      />
      {!collapsed && (
        <span
          className={cn('text-xs truncate', active ? 'font-semibold' : 'font-medium text-foreground')}
          style={active ? { color: accentColor } : undefined}
        >
          {item.label}
        </span>
      )}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" align="center">
          {item.label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}

function MenuSection({
  section,
  items,
  collapsed,
    isActive,
  getItemPath,
}: {
  section: SidebarMenuSection
  items: NavItem[]
  collapsed: boolean
  accentColor: string
  isActive: (item: NavItem) => boolean
  getItemPath: (item: NavItem) => string
}) {
  return (
    <div>
      {/* Section label */}
      {section.label && !collapsed && (
        <div className="px-2.5 pb-0.5 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {section.label}
        </div>
      )}
      {section.label && collapsed && <div className="h-1.5" />}

      {/* Items */}
      {items.map((item) => (
        <MenuItem
          key={item.id}
          item={item}
          active={isActive(item)}
          collapsed={collapsed}
          href={getItemPath(item)}
        />
      ))}
    </div>
  )
}

function MenuItem({
  item,
  active,
  collapsed,
  href,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  href: string
}) {
  const Icon = item.icon

  const link = (
    <Link
      to={href}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg border-l-[3px] px-2.5 py-[5.5px] transition-all no-underline',
        active
          ? 'border-l-[#1b449d] bg-[#1b449d]/15'
          : 'border-l-transparent hover:bg-sidebar-accent/50',
        collapsed && 'justify-center px-0 py-2'
      )}
    >
      <Icon
        className={cn('h-3.5 w-3.5 shrink-0', active ? 'text-[#1b449d]' : 'text-muted-foreground')}
        strokeWidth={1.6}
      />
      {!collapsed && (
        <>
          <span
            className={cn(
              'flex-1 text-left text-xs truncate',
              active ? 'font-semibold text-[#1b449d]' : 'text-muted-foreground'
            )}
          >
            {item.label}
          </span>
          {!item.implemented && (
            <Badge
              variant="outline"
              className="ml-auto rounded-full px-1.5 py-0 text-[10px] font-normal shrink-0 opacity-40"
            >
              Soon
            </Badge>
          )}
        </>
      )}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" align="center">
          {item.label}
          {!item.implemented && (
            <span className="ml-1 text-muted-foreground">(Coming Soon)</span>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}
